import {
  Badge,
  Button,
  Card,
  DatePicker,
  Descriptions,
  Divider,
  Flex,
  Form,
  Input,
  List,
  message,
  Modal,
  Space,
  Spin,
  Tag,
  Typography,
} from 'antd';
import { useContext, useEffect, useState } from 'react';
import { UserContext } from '../../UserContext';
import { BaseComponentProps } from '../../types';
import dayjs from 'dayjs';
import { formatDateWithOptionalTime, getAgentTimezone } from './utils';

const { Text, Title } = Typography;

interface ProactiveAction {
  id: string;
  agent_id: string;
  action: string;
  classifications: string[];
  action_data: any;
  scheduled_time: string | null;
  lead_id: string | null;
  source_id: string;
  source_type: string;
  sequence_number: number | null;
  status: string;
  created_at: string;
  updated_at: string;
  lead?: {
    id: string;
    first_name: string;
    last_name: string;
    phone?: string;
    email?: string;
    source_id?: string;
  };
  crm_task?: {
    id: string;
    title: string;
    description?: string;
    due_date?: string;
  };
  native_reminder?: {
    id: string;
    description: string;
    datetime: string;
  };
}

interface ProactiveActionsProps extends BaseComponentProps {
  onViewLead?: (leadId: string) => void;
}

export const ProactiveActions = ({
  agent,
  apiUrl,
  clientConfig,
  onViewLead,
}: ProactiveActionsProps): JSX.Element => {
  const user = useContext(UserContext);
  const [actions, setActions] = useState<ProactiveAction[] | undefined>(
    undefined,
  );
  const [loading, setLoading] = useState<boolean>(true);
  const [currentTime, setCurrentTime] = useState<Date>(new Date());
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
  const [selectedAction, setSelectedAction] = useState<ProactiveAction | null>(
    null,
  );
  const [isEditModalVisible, setIsEditModalVisible] = useState<boolean>(false);
  const [editingAction, setEditingAction] = useState<ProactiveAction | null>(
    null,
  );
  const [editForm] = Form.useForm();
  const [submitting, setSubmitting] = useState<boolean>(false);

  const agentTimezone = getAgentTimezone(agent, clientConfig);

  const fetchProactiveActions = async () => {
    try {
      setLoading(true);
      if (!user?.accessToken) {
        console.error('No access token available');
        return;
      }

      const url = `${apiUrl}/api/proactive-actions/agent/${agent.id}`;
      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${user.accessToken}`,
        },
      });

      if (!response.ok) {
        throw new Error(
          `Failed to fetch proactive actions: ${response.status}`,
        );
      }

      const jsonData = await response.json();
      setActions(jsonData);
    } catch (err) {
      console.error('An error occurred while fetching proactive actions:', err);
      setActions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async (actionId: string) => {
    try {
      if (!user?.accessToken) {
        return;
      }

      const url = `${apiUrl}/api/proactive-actions/${actionId}/cancel`;
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${user.accessToken}`,
        },
      });

      if (response.ok) {
        message.success('Action cancelled successfully');
        fetchProactiveActions();
      } else {
        message.error('Failed to cancel action');
      }
    } catch (err) {
      console.error('Error cancelling action:', err);
      message.error('An error occurred');
    }
  };

  useEffect(() => {
    fetchProactiveActions();

    // Update current time every minute to show agent's current time accurately
    const timer = setInterval(() => setCurrentTime(new Date()), 60000);
    return () => clearInterval(timer);
  }, [agent]);

  const openEditModal = (action: ProactiveAction) => {
    setEditingAction(action);
    editForm.setFieldsValue({
      scheduled_time: action.scheduled_time
        ? dayjs(action.scheduled_time)
        : dayjs().add(10, 'minutes'),
      message: action.action_data?.message || '',
    });
    setIsEditModalVisible(true);
  };

  const handleEditSubmit = async (values: {
    scheduled_time: dayjs.Dayjs;
    message?: string;
  }) => {
    try {
      setSubmitting(true);
      if (!editingAction || !user?.accessToken) {
        return;
      }

      const url = `${apiUrl}/api/proactive-actions/${editingAction.id}`;
      const response = await fetch(url, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${user.accessToken}`,
        },
        body: JSON.stringify({
          scheduled_time: values.scheduled_time.toISOString(),
          message: values.message,
        }),
      });

      if (response.ok) {
        message.success(
          `Action ${editingAction.status === 'cancelled' ? 'Enabled' : 'Updated'} successfully`,
        );
        setIsEditModalVisible(false);
        fetchProactiveActions();
      } else {
        const error = await response.json();
        message.error(error.error || 'Failed to update action');
      }
    } catch (err) {
      console.error('Error updating action:', err);
      message.error('An error occurred');
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusBadgeColor = (
    status: string,
  ): 'success' | 'error' | 'warning' | 'default' | 'processing' => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'cancelled':
        return 'error';
      case 'pending':
        return 'warning';
      default:
        return 'default';
    }
  };

  const showDetails = (action: any) => {
    setSelectedAction(action);
    setIsModalVisible(true);
  };

  if (!agent) {
    return <></>;
  }

  return (
    <div style={{ padding: '20px' }}>
      <Spin spinning={loading}>
        <Flex gap="middle" vertical>
          <Flex align="center" justify="space-between">
            <Button onClick={() => void fetchProactiveActions()} size="large">
              Refresh
            </Button>
            <div style={{ textAlign: 'right' }}>
              <Text strong style={{ fontSize: '12px', display: 'block' }}>
                All times displayed are local to agent
              </Text>
              <Text style={{ fontSize: '12px' }} type="secondary">
                Agent's current time:{' '}
                {formatDateWithOptionalTime(
                  currentTime.toISOString(),
                  agentTimezone,
                )}
              </Text>
            </div>
          </Flex>

          {actions && actions.length === 0 && (
            <Card style={{ textAlign: 'center' }}>
              <Text type="secondary">
                No proactive actions found for this agent.
              </Text>
            </Card>
          )}

          <List
            dataSource={actions || []}
            renderItem={action => (
              <Card
                bodyStyle={{ padding: '24px' }}
                style={{
                  marginBottom: '16px',
                  borderRadius: '12px',
                  border: '1px solid #f0f0f0',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                }}
              >
                <Flex align="start" justify="space-between">
                  <Flex gap="small" style={{ flex: 1 }} vertical>
                    <Flex align="center" gap="middle">
                      <Title level={4} style={{ margin: 0 }}>
                        Scheduled for:{' '}
                        {formatDateWithOptionalTime(
                          action.scheduled_time,
                          agentTimezone,
                        )}
                      </Title>
                      <Tag
                        color="blue"
                        style={{
                          borderRadius: '4px',
                          border: 'none',
                          padding: '0 12px',
                          textTransform: 'capitalize',
                        }}
                      >
                        Scheduled {action.action}
                      </Tag>
                    </Flex>

                    <Flex gap="small">
                      <Text style={{ fontSize: '12px' }} type="secondary">
                        ID: {action.id}
                      </Text>
                      {action.sequence_number && (
                        <Text style={{ fontSize: '12px' }} type="secondary">
                          | Seq: {action.sequence_number}
                        </Text>
                      )}
                    </Flex>

                    <div style={{ marginTop: '16px' }}>
                      <Text style={{ fontSize: '18px' }}>
                        {action.action === 'call' ? 'Call' : 'SMS'} with{' '}
                        <Text
                          onClick={() =>
                            action.lead?.source_id &&
                            onViewLead?.(action.lead.source_id)
                          }
                          style={{
                            color: '#722ed1',
                            cursor: 'pointer',
                            fontSize: '18px',
                            fontWeight: 500,
                          }}
                        >
                          {action.lead
                            ? `${action.lead.first_name} ${action.lead.last_name}`
                            : 'Unknown Lead'}
                        </Text>
                        {' - '}
                        {action.classifications?.join(', ') ||
                          'Proactive outreach'}
                      </Text>
                    </div>

                    <Space
                      split={<Text type="secondary">|</Text>}
                      style={{ marginTop: '12px' }}
                    >
                      <Text style={{ fontSize: '12px' }} type="secondary">
                        Created:{' '}
                        {formatDateWithOptionalTime(
                          action.created_at,
                          agentTimezone,
                        )}
                      </Text>
                      <Text style={{ fontSize: '12px' }} type="secondary">
                        Last Updated:{' '}
                        {formatDateWithOptionalTime(
                          action.updated_at,
                          agentTimezone,
                        )}
                      </Text>
                    </Space>
                  </Flex>

                  <Flex align="center" gap="small">
                    <Tag
                      color={getStatusBadgeColor(action.status)}
                      style={{
                        margin: 0,
                        padding: '4px 12px',
                        borderRadius: '4px',
                        textTransform: 'capitalize',
                        fontWeight: 500,
                      }}
                    >
                      {action.status}
                    </Tag>
                    <Button onClick={() => showDetails(action)} type="primary">
                      View
                    </Button>
                    {action.status === 'pending' && (
                      <Button onClick={() => openEditModal(action)}>
                        Edit
                      </Button>
                    )}
                    {action.status === 'cancelled' && (
                      <Button
                        onClick={() => openEditModal(action)}
                        type="primary"
                      >
                        Enable
                      </Button>
                    )}
                    {action.status === 'pending' && (
                      <Button
                        danger
                        onClick={() => void handleCancel(action.id)}
                      >
                        Cancel
                      </Button>
                    )}
                  </Flex>
                </Flex>
              </Card>
            )}
          />
        </Flex>
      </Spin>

      <Modal
        footer={[
          <Button key="close" onClick={() => setIsModalVisible(false)}>
            Close
          </Button>,
          selectedAction?.lead?.source_id && (
            <Button
              key="go-to-lead"
              onClick={() => {
                const sourceId = selectedAction.lead?.source_id;
                if (sourceId) {
                  setIsModalVisible(false);
                  onViewLead?.(sourceId);
                }
              }}
              type="primary"
            >
              Go to Lead
            </Button>
          ),
        ]}
        onCancel={() => setIsModalVisible(false)}
        open={isModalVisible}
        title={
          <Flex align="center" gap="small">
            <Title level={4} style={{ margin: 0 }}>
              Action Details
            </Title>
            {selectedAction && (
              <Tag
                color={getStatusBadgeColor(selectedAction.status)}
                style={{ textTransform: 'capitalize' }}
              >
                {selectedAction.status}
              </Tag>
            )}
          </Flex>
        }
        width={600}
      >
        {selectedAction && (
          <Flex gap="large" vertical>
            <Descriptions bordered column={1} size="small" title="Overview">
              <Descriptions.Item label="Type">
                <Tag color="blue" style={{ textTransform: 'capitalize' }}>
                  {selectedAction.action}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Scheduled For">
                {formatDateWithOptionalTime(
                  selectedAction.scheduled_time,
                  agentTimezone,
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Classifications">
                {selectedAction.classifications?.join(', ') || 'N/A'}
              </Descriptions.Item>
            </Descriptions>

            {selectedAction.action === 'sms' &&
              selectedAction.action_data?.message && (
                <div>
                  <Text strong>Message Content:</Text>
                  <Card
                    size="small"
                    style={{
                      marginTop: '8px',
                      backgroundColor: '#f9f9f9',
                      fontStyle: 'italic',
                    }}
                  >
                    <Text>"{selectedAction.action_data.message}"</Text>
                  </Card>
                </div>
              )}

            {selectedAction.crm_task && (
              <div>
                <Divider style={{ margin: '12px 0' }} />
                <Title level={5}>Linked CRM Task</Title>
                <Descriptions bordered column={1} size="small">
                  <Descriptions.Item label="Title">
                    <Text strong>{selectedAction.crm_task.title}</Text>
                  </Descriptions.Item>
                  <Descriptions.Item label="Description">
                    {selectedAction.crm_task.description ||
                      'No description provided.'}
                  </Descriptions.Item>
                </Descriptions>
              </div>
            )}

            {selectedAction.native_reminder && (
              <div>
                <Divider style={{ margin: '12px 0' }} />
                <Title level={5}>Reminder Context</Title>
                <Text>{selectedAction.native_reminder.description}</Text>
              </div>
            )}

            <Divider style={{ margin: '12px 0' }} />

            <Descriptions bordered column={1} size="small" title="Lead Details">
              <Descriptions.Item label="Name">
                {selectedAction.lead
                  ? `${selectedAction.lead.first_name} ${selectedAction.lead.last_name}`
                  : 'Unknown'}
              </Descriptions.Item>
              <Descriptions.Item label="Phone">
                {selectedAction.lead?.phone || 'N/A'}
              </Descriptions.Item>
              <Descriptions.Item label="Email">
                {selectedAction.lead?.email || 'N/A'}
              </Descriptions.Item>
            </Descriptions>

            <Text style={{ fontSize: '11px' }} type="secondary">
              Internal ID: {selectedAction.id}
              <br />
              {selectedAction.sequence_number && (
                <>
                  Sequence Number: {selectedAction.sequence_number}
                  <br />
                </>
              )}
              Created:{' '}
              {formatDateWithOptionalTime(
                selectedAction.created_at,
                agentTimezone,
              )}
              <br />
              Last Updated:{' '}
              {formatDateWithOptionalTime(
                selectedAction.updated_at,
                agentTimezone,
              )}
            </Text>
          </Flex>
        )}
      </Modal>

      <Modal
        confirmLoading={submitting}
        okText={editingAction?.status === 'cancelled' ? 'Enable' : 'Update'}
        onCancel={() => setIsEditModalVisible(false)}
        onOk={() => editForm.submit()}
        open={isEditModalVisible}
        title={
          editingAction?.status === 'cancelled'
            ? 'Enable Proactive Action'
            : 'Edit Proactive Action'
        }
      >
        <Form
          form={editForm}
          layout="vertical"
          onFinish={values => {
            void handleEditSubmit(values);
          }}
        >
          <Form.Item
            extra="All times are local to the agent."
            label="Scheduled Date & Time"
            name="scheduled_time"
            rules={[{ required: true, message: 'Please select a time' }]}
          >
            <DatePicker
              format="YYYY-MM-DD HH:mm:ss" 
              showTime 
              style={{ width: '100%' }}
              // Allow selecting any time in the future
              disabledDate={(current) => current && current < dayjs().startOf('day')}
            />
          </Form.Item>

          {editingAction?.action === 'sms' && (
            <Form.Item
              label="SMS Message"
              name="message"
              rules={[
                { required: true, message: 'Message content is required' },
              ]}
            >
              <Input.TextArea rows={4} />
            </Form.Item>
          )}

          <div style={{ marginTop: '16px' }}>
            <Text style={{ fontSize: '12px' }} type="secondary">
              Current timezone: {agentTimezone}
            </Text>
          </div>
        </Form>
      </Modal>
    </div>
  );
};
